import { areRangesOverlapping, max, min } from 'date-fns';
import { MediaItem, NormalizedMedias, TreeItem } from '../store';
import { EVENTS_NODE_NAME, LOOP_NODE_NAME, MediaType } from '../store/types';
import * as DateUtils from '../viewModes/utils';

interface Node {
  item: TreeItem;
  children: Node[];
}

/**
 * We need to group all medias under two categories (Events and Loops). We will do this by
 * inserting two virtual medias/nodes in the flattenedTree and 2 respective elements in the
 * mediasArray.
 *
 * With the ajax responses comming from the server, separate the flattenedTree array into 2 separate
 * arrays: one containing event medias from the channel and the other everything else (calling the rest loop media).
 *
 * The we continue to update all elements in flattenedTree. We increment the level and update
 *
 * @param rootId id of the channel media
 * @param mediasArray list of media info (id, name, stard/end date etc.)
 * @param flattenedTree list of basic tree items, ordered by level
 */
const groupBackendData = (
  rootId: number,
  mediasArray: MediaItem[],
  flattenedTree: TreeItem[]
): { mediasArray: MediaItem[]; flattenedTree: TreeItem[] } => {
  if (mediasArray.length < 2) {
    return { mediasArray, flattenedTree };
  }

  let eventTreeItems: TreeItem[] = [];
  let loopTreeItems: TreeItem[] = [];
  let maxMediaId: number = 0;

  /**
   * We need to generate media IDs for the virtual media 'Events' and 'Loops'.
   * We reduce the mediasArray and retain the largest media.id value we can find.
   *
   * We increment the maxMediaId and use it for loopsWrapperTreeItem, eventsWrapperTreeItem,
   * loopsMediaWrapper and eventsMediaWrapper.
   */
  maxMediaId = mediasArray.reduce((value: number, media: MediaItem): number => {
    return media.id > value ? media.id : value;
  }, 0);

  const loopMediaId = ++maxMediaId;
  const eventMediaId = ++maxMediaId;

  // this will be inserted in the flattenedTree as the root of the Loop medias
  const loopsWrapperTreeItem: any = {
    id: loopMediaId,
    level: 1,
    mediaParentId: rootId,
    porder: 0,
    uniqueID: LOOP_NODE_NAME,
    uniquePARENTID: `${rootId}`
  };

  // this will be inserted in the flattenedTree as the root of the Event medias
  const eventsWrapperTreeItem: any = {
    id: eventMediaId,
    level: 1,
    mediaParentId: rootId,
    porder: 1,
    uniqueID: EVENTS_NODE_NAME,
    uniquePARENTID: `${rootId}`
  };

  // this will be inserted in the mediasArray as the description for the loopsWrapperTreeItem node
  const loopsMediaWrapper: any = {
    id: loopMediaId,
    startDate: '2099-12-31T00:00:00',
    endDate: '3099-12-31T00:00:00',
    diffusionDays: 127,
    endTime: '23:59:59',
    startTime: '00:00:00',
    mediaType: MediaType.None,
    name: LOOP_NODE_NAME
  };

  // this will be inserted in the mediasArray as the description for the eventsWrapperTreeItem node
  const eventsMediaWrapper: any = {
    id: eventMediaId,
    startDate: '2099-12-31T00:00:00',
    endDate: '3099-12-31T00:00:00',
    diffusionDays: 127,
    endTime: '23:59:59',
    startTime: '00:00:00',
    mediaType: MediaType.None,
    name: EVENTS_NODE_NAME
  };

  // normalize all media descriptions so we can easily filter/search/etc. by id
  const normalizedMedias = mediasArray.reduce((obj, media) => {
    obj[media.id] = media;
    return obj;
  }, {});

  // select all medias from flattenedTree which have mediaType == EventPlaylist in normalizedMedias
  eventTreeItems = flattenedTree.filter((mediaTreeItem: TreeItem) => {
    return (
      mediaTreeItem.mediaParentId === rootId && normalizedMedias[mediaTreeItem.id].mediaType === MediaType.EventPlaylist
    );
  });

  // loop through flattenedTree and gather all descendants for the medias in eventTreeItems (event type media)
  while (true) {
    const currentLength = eventTreeItems.length;
    const newEventTreeItems: TreeItem[] = flattenedTree.filter((mediaTreeItem: TreeItem) => {
      return (
        eventTreeItems.indexOf(mediaTreeItem) < 0 &&
        eventTreeItems.findIndex((localMedia: TreeItem) => localMedia.id === mediaTreeItem.mediaParentId) > -1
      );
    });

    eventTreeItems = eventTreeItems.concat(newEventTreeItems);

    // if at the end of the iteration the length is the same as the beginning it's a sign to break
    if (eventTreeItems.length === currentLength) {
      break;
    }
  }

  // the difference flattenedTree - eventTreeItems === loopTreeItems (so we select all medias that are NOT EventPlaylist)
  loopTreeItems = flattenedTree.filter((mediaTreeItem: TreeItem) => eventTreeItems.indexOf(mediaTreeItem) < 0);

  /**
   * Now that we have the medias separated in 2 arrays we map
   * all the media from flattenedTree to update the following
   * properties: level, uniqueID and uniquePARENTID.
   */
  const newTreeItems = flattenedTree.map((mediaTreeItem: TreeItem) => {
    if (mediaTreeItem.id === rootId) {
      return mediaTreeItem;
    }

    if (loopTreeItems.indexOf(mediaTreeItem) > -1) {
      let temp = mediaTreeItem.uniqueID.split('-');
      temp.splice(-1, 1, loopsWrapperTreeItem.uniqueID);
      mediaTreeItem.uniqueID = temp.join('-');
      temp = mediaTreeItem.uniquePARENTID.split('-');
      temp.splice(-1, 1, loopsWrapperTreeItem.uniqueID);
      mediaTreeItem.uniquePARENTID = temp.join('-');
    }

    if (eventTreeItems.indexOf(mediaTreeItem) > -1) {
      let temp = mediaTreeItem.uniqueID.split('-');
      temp.splice(-1, 1, eventsWrapperTreeItem.uniqueID);
      mediaTreeItem.uniqueID = temp.join('-');
      temp = mediaTreeItem.uniquePARENTID.split('-');
      temp.splice(-1, 1, eventsWrapperTreeItem.uniqueID);
      mediaTreeItem.uniquePARENTID = temp.join('-');
    }

    mediaTreeItem.level++;
    return mediaTreeItem;
  });

  // NOW we insert the virtual media nodes in the server response
  if (loopTreeItems.length > 0) {
    mediasArray.splice(1, 0, loopsMediaWrapper);
    newTreeItems.splice(1, 0, loopsWrapperTreeItem);
  }

  if (eventTreeItems.length > 0) {
    mediasArray.splice(2, 0, eventsMediaWrapper);
    newTreeItems.splice(2, 0, eventsWrapperTreeItem);
  }

  return { mediasArray, flattenedTree: newTreeItems };
};

export const transformBackendData = (
  rootId: number,
  mediasArray: MediaItem[],
  flattenedTree: TreeItem[]
): { medias: NormalizedMedias; tree: TreeItem[] } => {
  ({ mediasArray, flattenedTree } = groupBackendData(rootId, mediasArray, flattenedTree));

  const medias = mediasArray.reduce((obj, media) => {
    const sds = media.startDate.toString();
    const sd = sds.substr(0, sds.indexOf('T') + 1) + media.startTime.toString();
    const eds = media.endDate.toString();
    const ed = eds.substr(0, eds.indexOf('T') + 1) + media.endTime.toString();
    media.startDate = new Date(sd);
    media.endDate = new Date(ed);
    media.startTime = DateUtils.getTimeOnly(media.startDate);
    media.endTime = DateUtils.getTimeOnly(media.endDate);
    obj[media.id] = media;
    return obj;
  }, {});

  const builtUpTree = buildUpTree(rootId, flattenedTree);
  const tree: TreeItem[] = depthFirst(medias, builtUpTree);

  return { medias, tree };
};

/**
 * Construct the hierarchical tree structure from the flat list received from the backend. This is done since the timeline needs the list in a particular order, plus some other computations are required based on a node's list of parents
 * @param rootId id of the channel media
 * @param flattenedTree list of basic tree items, ordered by level
 */
const buildUpTree = (rootId: number, flattenedTree: TreeItem[]): Node => {
  const rootKey = `${rootId}`;
  const lookup: { [id: string]: Node } = {
    [rootKey]: {
      item: flattenedTree[0],
      children: []
    }
  };
  lookup[rootKey].item.treeId = '';

  for (let i = 1; i < flattenedTree.length; i++) {
    const parent = lookup[flattenedTree[i].uniquePARENTID];
    if (!parent) {
      continue;
    }
    const child = { item: flattenedTree[i], children: [] };
    parent.children.push(child);
    parent.item.hasChildren = true;
    child.item.isExpanded = false;
    lookup[child.item.uniqueID] = child;
    child.item.treeId = parent.item.treeId + '/' + parent.children.length;
  }
  return lookup[rootKey];
};

/*
In case we need to get the parent by mediaParentId and porder
const getParentByOrder = (
  nodeLookup: { [id: string]: Node },
  parentId: number,
  childId: number,
  childOrder: number
): Node | null => {
  const parentIdString = parentId.toString();
  const parentKeys = Object.keys(nodeLookup).filter(k => k.startsWith(parentIdString));
  for (const parentKey of parentKeys) {
    const parent = nodeLookup[parentKey];
    const child = parent.children.find(c => c.item.id === childId && c.item.porder === childOrder);
    if (!child) {
      return parent;
    }
  }
  return null;
};
*/

/**
 * Flatten the tree using a Dept-First-Search. This is needed for the timeline which can only display a flat list
 * @param medias all the medias normalized into an object where the key is the id of the media and the value is the media itself
 * @param node current node. This is a recursive function so it starts with the root and goes through the entire tree.
 */
const depthFirst = (medias: NormalizedMedias, node: Node): TreeItem[] => {
  let subtree: TreeItem[] = [];

  // tslint:disable-next-line:prefer-for-of
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];

    const { startDate, endDate, startTime, endTime } = computeNodeDateRangeBasedOnParent(medias, node, child);
    child.item.startDate = startDate;
    child.item.endDate = endDate;
    child.item.startTime = startTime;
    child.item.endTime = endTime;
    child.item.diffusionDays = computeNodeDiffusionDaysBasedOnParent(medias, node, child);

    subtree.push(child.item);
    subtree = subtree.concat(depthFirst(medias, child));
  }
  return subtree;
};

/**
 * Compute the overlapping of media startDate and endDate between the child and parent. This is needed in order to properly display the timeline duration bars.
 * Due to the nature of parent/child relation, a media's range will be included in the "parent valid range" calculation.
 * Ex: tag1 --> playlist1 --> media1. The "parent valid range" form media1 is the intersection between tag1, playlist1 AND media1.
 * @param medias all the medias normalized into an object where the key is the id of the media and the value is the media itself
 * @param parent the parent node
 * @param child the child node
 */
const computeNodeDateRangeBasedOnParent = (
  medias: NormalizedMedias,
  parent: Node,
  child: Node
): { startDate: Date | null; endDate: Date | null; startTime: Date | null; endTime: Date | null } => {
  const childMedia = medias[child.item.id];
  if (parent.item.level < 2) {
    // level 0 is the channel and it doesn't have a date range
    // level 1 is 'Loops' and 'Events' which are virtual medias
    return {
      startDate: childMedia.startDate,
      endDate: childMedia.endDate,
      startTime: DateUtils.getTimeOnly(childMedia.startDate),
      endTime: DateUtils.getTimeOnly(childMedia.endDate)
    };
  }

  let startDate: Date | null = null;
  let endDate: Date | null = null;
  if (parent.item.startDate && parent.item.endDate) {
    if (!areRangesOverlapping(parent.item.startDate, parent.item.endDate, childMedia.startDate, childMedia.endDate)) {
      return { startDate: null, endDate: null, startTime: null, endTime: null };
    }
    startDate = max(parent.item.startDate, childMedia.startDate);
    endDate = min(parent.item.endDate, childMedia.endDate);
  }

  // compute the time intersections
  let startTime = null;
  let endTime = null;

  if (parent.item.startTime && parent.item.endTime) {
    if (areRangesOverlapping(parent.item.startTime, parent.item.endTime, childMedia.startTime, childMedia.endTime)) {
      startTime = max(parent.item.startTime, childMedia.startTime);
      endTime = min(parent.item.endTime, childMedia.endTime);
    }
  }
  // they overlap so return the intersection between the two ranges(parent and child)
  return {
    startDate,
    endDate,
    startTime,
    endTime
  };
};

const computeNodeDiffusionDaysBasedOnParent = (medias: NormalizedMedias, parent: Node, child: Node): number => {
  const childMedia = medias[child.item.id];

  if (parent.item.level < 2) {
    // level 0 is the channel and it doesn't have a date range
    // level 1 is 'Loops' and 'Events' which are virtual medias
    return childMedia.diffusionDays;
  }
  return childMedia.diffusionDays & parent.item.diffusionDays;
};



// WEBPACK FOOTER //
// ./src/utils/treeTransform.ts